import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ReallocationSource, ReallocationTarget } from '../../../types/reallocation/Reallocation'
import { updateReallocationTarget } from '../service/reallocationService'
import { useContextParameters } from '../../../services/api/hooks/useContextParameters'
import reallocationQueryKeys from './reallocationQueryKeys'
import invalidateReportQueries from './invalidateReportQueries'

interface UpdateReallocationTargetVariables {
  target: ReallocationTarget
  source: ReallocationSource
}

export const useUpdateReallocationTarget = () => {
  const { companyId } = useContextParameters()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ target, source }: UpdateReallocationTargetVariables) => {
      if (!companyId) {
        throw new Error('Company ID is required')
      }
      const updatedTarget = await updateReallocationTarget(target, companyId, source.id)
      return { target: updatedTarget, source }
    },
    onSuccess: ({ target, source }) => {
      queryClient.setQueryData<ReallocationSource[]>(reallocationQueryKeys.list({ companyId }), old => {
        if (!Array.isArray(old)) return old
        return old.map(item =>
          item.id === source.id
            ? {
                ...item,
                targets: item.targets.map(t => (t.id === target.id ? { ...t, ...target } : t))
              }
            : item
        )
      })
      invalidateReportQueries(queryClient)
    },
    onError: error => {
      console.error('Failed to update reallocation target:', error)
    }
  })
}
